import { useEffect, useMemo, useCallback, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery, useQueryLocation, useAuth, useWorkoutImage } from '../hooks';
import { getHabitTrackerData } from '../lib/habitTrackerHelpers';
import useStorefrontAPI from '../hooks/use-storefront-api';
import { STOREFRONT_QUERIES, transformVideos } from '../lib/storefrontClient';
import { filterAndSortVideos } from '../utils/video-filters';
import Badge from '../components/Badge';
import Link from '../components/Link';
import BadgeCelebrationModal from '../components/BadgeCelebrationModal';
import WorkoutImage from '../components/WorkoutImage';
import Header from '../components/Header';

function BadgesPage() {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTabletPortrait = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
    const [location] = useQueryLocation();
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    
    // Reusable class names
    const oswaldHeadingClasses = "tw-text-black tw-font-['Anton'] tw-font-normal tw-leading-[110%] tw-uppercase tw-m-0";
    const responsivePaddingClasses = (isMobile || isTabletPortrait) ? 'tw-px-2.5' : 'tw-px-5';
    const cardContainerClasses = clsx(
        'tw-flex tw-flex-col tw-gap-6 tw-self-stretch tw-max-w-[930px] tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-p-6',
        (isMobile || isTabletPortrait) ? 'tw-w-full' : 'tw-w-full'
    );
    const sectionWrapperClasses = `tw-flex tw-justify-center tw-bg-transparent tw-mb-14 ${responsivePaddingClasses}`;

    const { isAuthenticated, logout, isLoading: authLoading, hasActiveSubscription, subscriptionLoading } = useAuth();
    
    // Get shared workout image
    const { thumbnailUrl } = useWorkoutImage();
    
    // Fetch habit tracker data from Liquid template
    const [habitTrackerData, setHabitTrackerData] = useState({ 
        currentStreak: 0, 
        personalRecord: 0, 
        badges: [] 
    });
    const [habitTrackerLoading, setHabitTrackerLoading] = useState(true);
    const [dataRefreshKey, setDataRefreshKey] = useState(0);

    // Check for completed workout and trigger refresh
    useEffect(() => {
        const completedDate = sessionStorage.getItem('completedWorkoutDate');
        if (completedDate) {
            // Clear the flag so we don't keep refreshing
            sessionStorage.removeItem('completedWorkoutDate');
            // Trigger a data refresh
            setDataRefreshKey(prev => prev + 1);
        }
    }, []);
    
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            getHabitTrackerData()
                .then(data => {
                    setHabitTrackerData(data);
                    setHabitTrackerLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load habit tracker data:', err);
                    setHabitTrackerLoading(false);
                });
        }
    }, [isAuthenticated, authLoading, dataRefreshKey]);
    
    // Fetch badge metaobjects to get images
    const [badgeMetaobjects, setBadgeMetaobjects] = useState([]);
    useEffect(() => {
        const fetchBadgeMetaobjects = async () => {
            try {
                const response = await fetch(`https://${window.Shopify?.shop || window.location.hostname}/api/2024-01/graphql.json`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': '4894686455994e9f30ec76fc0246ad24',
                    },
                    body: JSON.stringify({
                        query: `
                            query getBadges {
                                metaobjects(type: "badge", first: 50) {
                                    edges {
                                        node {
                                            handle
                                            fields {
                                                key
                                                value
                                                reference {
                                                    ... on MediaImage {
                                                        image {
                                                            url
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        `
                    })
                });
                
                const result = await response.json();
                if (result.data?.metaobjects?.edges) {
                    const badges = result.data.metaobjects.edges.map(({ node }) => {
                        const fields = {};
                        node.fields.forEach(field => {
                            if (field.key === 'image' && field.reference?.image?.url) {
                                fields.image = field.reference.image.url;
                            } else {
                                fields[field.key] = field.value;
                            }
                        });
                        return {
                            handle: node.handle,
                            fields
                        };
                    });
                    
                    // Log streak badges for debugging
                    const streakBadges = badges.filter(b => b.handle.includes('day-streak'));
                    
                    setBadgeMetaobjects(badges);
                }
            } catch (error) {
                console.error('Failed to fetch badge metaobjects:', error);
            }
        };
        
        fetchBadgeMetaobjects();
    }, []);
    
    // Show loading/redirect state
    if (!isAuthenticated || subscriptionLoading) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-gray-900 tw-mb-4"></div>
                    <p>{!isAuthenticated ? 'Logging out...' : 'Loading...'}</p>
                </div>
            </div>
        );
    }

    // Check for active subscription
    if (!hasActiveSubscription) {
        return (
            <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50">
                <div className="tw-max-w-md tw-w-full tw-mx-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
                        <div className="tw-mb-6">
                            <div className="tw-text-6xl tw-mb-4">🔒</div>
                            <h1 className="tw-text-3xl tw-font-bold tw-mb-2">Subscription Required</h1>
                            <p className="tw-text-gray-600">You need an active subscription to access your badges and achievements.</p>
                        </div>
                        
                        <a 
                            href="https://lexijwellness.com/products/ljw-guided-workouts?variant=45633267237060&selling_plan=3552968900"
                            className="tw-inline-block tw-w-full tw-bg-black tw-text-white tw-py-3 tw-px-6 tw-rounded-lg tw-font-semibold tw-hover:bg-gray-800 tw-transition-colors tw-mb-4"
                        >
                            Get Subscription
                        </a>
                        
                        <button 
                            onClick={logout}
                            className="tw-w-full tw-bg-gray-200 tw-text-gray-800 tw-py-3 tw-px-6 tw-rounded-lg tw-font-semibold tw-hover:bg-gray-300 tw-transition-colors"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    // Memoize computed values - data is already parsed from getHabitTrackerData
    const currentStreak = habitTrackerData.currentStreak;
    const userBadges = habitTrackerData.badges;
    
    const formattedDate = useMemo(() => {
        const today = new Date();
        return today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
    }, []);

    // Use badge metaobjects as the source of all available badges (excluding daily streak badges)
    const allAvailableBadges = useMemo(() => {
        return badgeMetaobjects
            .filter(metaobject => !metaobject.handle.includes('day-streak') && !metaobject.handle.includes('daily-streak'))
            .map(metaobject => ({
                handle: metaobject.handle,
                name: metaobject.fields?.name || metaobject.handle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                description: metaobject.fields?.description || '',
                fields: metaobject.fields || {},
                image: metaobject.fields?.image
            }));
    }, [badgeMetaobjects]);

    // Merge earned badges with all available badges
    const displayBadges = useMemo(() => {
        // userBadges is now an array of objects like [{handle: '10-workouts-badge', earnedDate: '2024-01-15T...'}]
        // or could still be strings for backward compatibility
        const earnedBadgesMap = new Map();
        userBadges.forEach(badge => {
            if (typeof badge === 'string') {
                earnedBadgesMap.set(badge, null); // Old format, no date
            } else {
                earnedBadgesMap.set(badge.handle, badge.earnedDate);
            }
        });
        
        const badges = allAvailableBadges.map(availableBadge => {
            const isEarned = earnedBadgesMap.has(availableBadge.handle);
            const earnedDate = earnedBadgesMap.get(availableBadge.handle);
            
            return {
                ...availableBadge,
                locked: !isEarned,
                earnedDate: earnedDate
            };
        });
        
        // Sort: unlocked badges first (sorted by earned date, most recent first), then locked badges
        return badges.sort((a, b) => {
            // Unlocked badges come first
            if (!a.locked && b.locked) return -1;
            if (a.locked && !b.locked) return 1;
            
            // Both unlocked: sort by earned date (oldest first)
            if (!a.locked && !b.locked) {
                if (a.earnedDate && b.earnedDate) {
                    return new Date(a.earnedDate) - new Date(b.earnedDate);
                }
                // If one has a date and the other doesn't, the one with date comes first
                if (a.earnedDate && !b.earnedDate) return -1;
                if (!a.earnedDate && b.earnedDate) return 1;
            }
            
            return 0;
        });
    }, [userBadges, allAvailableBadges]);

    // TODO: Implement subscription badge checking using Customer Account API
    // This would require querying subscription data from Recharge or Shopify
    // and comparing against badge criteria

    const isActive = useMemo(() => (page) => {
        if (page === 'badges') return location === '/badges';
        if (page === 'calendar') return location === '/calendar';
        if (page === 'videos') return location === '/';
        return false;
    }, [location]);

    const handleLogoutClick = useCallback(() => {
        setShowLogoutConfirm(true);
    }, []);

    const handleLogoutConfirm = useCallback(() => {
        setShowLogoutConfirm(false);
        logout();
    }, [logout]);

    const handleLogoutCancel = useCallback(() => {
        setShowLogoutConfirm(false);
    }, []);

    const navLinks = useMemo(() => [
        { href: '/', label: 'Videos', page: 'videos', roundedClass: 'tw-rounded-l-lg', internal: true },
        { href: '/badges', label: 'Badges', page: 'badges', internal: true },
        { href: '/calendar', label: 'Calendar', page: 'calendar', internal: true },
        { href: 'https://www.facebook.com/share/g/1ADj3sTSs8/', label: 'Facebook Group', page: null, internal: false },
        { href: '/account', label: 'Account', page: null, internal: false, onClick: () => { sessionStorage.removeItem('was_on_habit_tracker'); window.location.href = '/account'; } },
        { label: 'Logout', page: null, roundedClass: 'tw-rounded-r-lg', internal: false, onClick: handleLogoutClick }
    ], [handleLogoutClick]);

    return (
        <>
            {/* Header */}
            <Header currentStreak={currentStreak} />
            
            <div className="tw-w-full tw-flex tw-justify-center">
                <div className="tw-w-full tw-max-w-[1233px] tw-mx-auto">
                    {/* Desktop Navigation Bar */}
                    {!isMobile && (
                    <nav className="tw-flex tw-justify-center tw-items-center tw-w-full tw-my-14 tw-p-0" style={{display: 'flex'}}>
                        <div className="tw-flex tw-justify-center tw-items-center">
                            {navLinks.map((link, index) => 
                                link.internal ? (
                                    <Link
                                        key={index}
                                        href={link.href} 
                                        className={clsx(
                                            "nav-link",
                                            link.roundedClass,
                                            link.page && isActive(link.page) ? "nav-link-active" : "nav-link-inactive"
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                ) : link.onClick ? (
                                    <button
                                        key={index}
                                        onClick={link.onClick}
                                        className={clsx(
                                            "nav-link",
                                            link.roundedClass,
                                            "nav-link-inactive",
                                            "tw-cursor-pointer"
                                        )}
                                    >
                                        {link.label}
                                    </button>
                                ) : (
                                    <a 
                                        key={index}
                                        href={link.href} 
                                        className={clsx(
                                            "nav-link",
                                            link.roundedClass,
                                            "nav-link-inactive"
                                        )}
                                    >
                                        {link.label}
                                    </a>
                                )
                            )}
                        </div>
                    </nav>
                )}

                {/* Active Streak Section */}
                {!isMobile && (
                <div className={`tw-w-full tw-flex tw-justify-center tw-mb-14 ${responsivePaddingClasses}`}>
                    <div className="tw-max-w-full tw-h-auto tw-rounded-[20px] tw-bg-[#F7F8F8] tw-w-full">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-0 tw-flex-row">
                            {/* Left Side - Active Streak */}
                            <div className={`tw-flex-1 tw-flex tw-flex-col tw-justify-center tw-items-center tw-py-[18px] ${isTabletPortrait ? 'tw-h-[281px]' : 'tw-h-[403px]'}`}>
                                {currentStreak > 0 && (
                                    <h1 className={clsx(oswaldHeadingClasses, 'tw-text-5xl tw-mb-8')}>
                                        ACTIVE STREAK
                                    </h1>
                                )}
                                
                                {/* Current Streak Badge */}
                                {currentStreak > 0 && (() => {
                                    const genericStreakBadge = badgeMetaobjects.find(b => b.handle === 'daily-streak-badge');
                                    
                                    const streakBadgeData = genericStreakBadge ? {
                                        handle: 'daily-streak-badge',
                                        name: `${currentStreak} Day Streak`,
                                        description: `Active streak of ${currentStreak} ${currentStreak === 1 ? 'day' : 'days'}`,
                                        fields: genericStreakBadge.fields,
                                        image: genericStreakBadge.fields?.image,
                                        locked: false,
                                        streakNumber: currentStreak
                                    } : null;
                                    
                                    return (
                                        <div className="tw-mb-8">
                                            <div 
                                                className="tw-relative tw-cursor-pointer"
                                                onClick={() => streakBadgeData && setSelectedBadge(streakBadgeData)}
                                            >
                                                <Badge 
                                                    icon="🔥"
                                                    title={`${currentStreak} Day Streak`}
                                                    date={formattedDate}
                                                    badge={streakBadgeData}
                                                />
                                                {/* Overlay the streak number */}
                                                {genericStreakBadge && (
                                                    <div className="tw-absolute tw-bg-white tw-rounded-full tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-black tw-font-bold tw-text-sm" style={{ top: 'calc(12% + 1.5px)', right: '35%' }}>
                                                        {currentStreak}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                <div className="tw-flex tw-flex-col tw-items-start tw-max-w-[400px]">
                                    <h2 className="heading-oswald tw-text-[22px] tw-mb-4 tw-text-left">
                                        {currentStreak > 0 
                                            ? "DON'T LOSE YOUR STREAK!\nSTART TODAY'S WORKOUT"
                                            : "START A NEW STREAK TODAY!"
                                        }
                                    </h2>
                                    
                                    <button 
                                        onClick={() => {
                                            sessionStorage.setItem('autoplayVideo', 'true');
                                            window.location.href = '/?habit-tracker';
                                        }}
                                        className="btn-action btn-action-lg tw-w-[300px]"
                                    >
                                        START WORKOUT
                                    </button>
                                </div>
                            </div>
                            
                            {/* Right Side - Image */}
                            <div className="tw-flex tw-flex-col tw-items-start">
                                <WorkoutImage
                                    thumbnailUrl={thumbnailUrl}
                                    className={`tw-object-cover tw-rounded-lg ${isTabletPortrait ? 'tw-w-[500px] tw-h-[281px]' : 'tw-w-[717px] tw-h-[403px]'}`}
                                    onClick={() => {
                                        sessionStorage.setItem('autoplayVideo', 'true');
                                        window.location.href = '/?habit-tracker';
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {/* Mobile Active Streak & Personal Record Section - Side by Side */}
                {isMobile && (
                <div className={`tw-w-full tw-flex tw-justify-center tw-mb-14 ${responsivePaddingClasses}`}>
                    <div className={cardContainerClasses}>
                        <div className={`tw-flex tw-gap-4 tw-mb-6 ${(currentStreak > 0 && habitTrackerData.personalRecord > 0) ? 'tw-justify-center' : 'tw-justify-center'}`}>
                            {/* Active Streak Column */}
                            {currentStreak > 0 && (
                                <div className={`tw-flex tw-flex-col tw-items-center ${(currentStreak > 0 && habitTrackerData.personalRecord > 0) ? 'tw-flex-1 tw-max-w-[150px]' : ''}`}>
                                    <div className="tw-flex tw-flex-col tw-items-center tw-w-full">
                                        {(currentStreak > 0 && habitTrackerData.personalRecord > 0) && (
                                            <h1 className="heading-oswald tw-leading-[115%] tw-text-[20px] tw-text-center tw-mb-2">
                                                Active Streak
                                            </h1>
                                        )}
                                        {(() => {
                                            const genericStreakBadge = badgeMetaobjects.find(b => b.handle === 'daily-streak-badge');
                                            
                                            const streakBadgeData = genericStreakBadge ? {
                                                handle: 'daily-streak-badge',
                                                name: `${currentStreak} Day Streak`,
                                                description: `Active streak of ${currentStreak} ${currentStreak === 1 ? 'day' : 'days'}`,
                                                fields: genericStreakBadge.fields,
                                                image: genericStreakBadge.fields?.image,
                                                locked: false,
                                                streakNumber: currentStreak
                                            } : null;
                                            
                                            return (
                                                <div className="tw-relative tw-cursor-pointer"
                                                    onClick={() => streakBadgeData && setSelectedBadge(streakBadgeData)}
                                                >
                                                    <Badge 
                                                        icon="🔥"
                                                        title={`${currentStreak} Day`}
                                                        date="STREAK"
                                                        badge={streakBadgeData}
                                                        size="small"
                                                    />
                                                    {/* Overlay the streak number */}
                                                    {genericStreakBadge && (
                                                        <div className="tw-absolute tw-top-[14%] tw-right-[31%] tw-bg-white tw-rounded-full tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-black tw-font-bold tw-text-base">
                                                            {currentStreak}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                            
                            {/* Personal Record Column */}
                            {habitTrackerData.personalRecord > 0 && (
                                <div className={`tw-flex tw-flex-col tw-items-center ${(currentStreak > 0 && habitTrackerData.personalRecord > 0) ? 'tw-flex-1 tw-max-w-[150px]' : ''}`}>
                                    <div className="tw-flex tw-flex-col tw-items-center tw-w-full">
                                        {(currentStreak > 0 && habitTrackerData.personalRecord > 0) && (
                                            <h1 className="heading-oswald tw-leading-[115%] tw-text-[20px] tw-text-center tw-mb-2">
                                                Personal Record
                                            </h1>
                                        )}
                                        <Badge 
                                            icon="🏆"
                                            title={`${habitTrackerData.personalRecord} Day`}
                                            date="RECORD"
                                            size="small"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                )}

                {/* Personal Record - Desktop Only */}
                {!isMobile && (
                <div className={sectionWrapperClasses}>
                    <div className={cardContainerClasses}>
                        <h1 className={clsx("heading-oswald tw-leading-[115%]", isMobile ? 'tw-text-[20px] tw-text-center' : 'tw-text-[40px] tw-text-center')}>
                            Personal Record
                        </h1>
                        
                        <div className={`tw-flex ${isMobile ? 'tw-justify-center' : 'tw-justify-center'}`}>
                            <Badge 
                                icon="🏆"
                                title={`${habitTrackerData.personalRecord} Day Streak`}
                                date={habitTrackerData.personalRecordDate 
                                    ? new Date(habitTrackerData.personalRecordDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()
                                    : formattedDate
                                }
                            />
                        </div>
                    </div>
                </div>
                )}

                {/* Trophy Case */}
                <div className={`${sectionWrapperClasses} tw-pb-20`}>
                    <div className={cardContainerClasses}>
                        <h1 className={clsx("heading-oswald tw-leading-[115%]", isMobile ? 'tw-text-[20px] tw-text-center' : 'tw-text-[40px] tw-text-center')}>
                            Trophy Case
                        </h1>
                        
                        {habitTrackerLoading ? (
                            <div className="tw-text-center tw-py-12">
                                <p className="tw-text-gray-500">Loading badges...</p>
                            </div>
                        ) : (
                            <div className={`tw-flex tw-flex-wrap ${isMobile ? 'tw-justify-center' : 'tw-justify-center'}`} style={{gap: '15.5px'}}>
                                {/* Other earned badges */}
                                {displayBadges.map((badge, index) => (
                                    <div 
                                        key={badge.id || badge.handle || index}
                                        onClick={() => !badge.locked && setSelectedBadge(badge)}
                                        className={!badge.locked ? 'tw-cursor-pointer' : ''}
                                    >
                                        <Badge 
                                            badge={badge}
                                            locked={badge.locked}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>


            </div>

            {/* Badge Celebration Modal */}
            {selectedBadge && !selectedBadge.locked && (
                <BadgeCelebrationModal 
                    badges={[selectedBadge]}
                    onClose={() => setSelectedBadge(null)}
                    currentStreak={currentStreak}
                />
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="modal-overlay tw-z-[1002]" onClick={handleLogoutCancel}>
                    <div className="modal-container tw-items-center tw-text-center" onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={handleLogoutCancel}
                            className="tw-absolute tw-top-6 tw-right-6 tw-bg-transparent tw-border-0 tw-text-3xl tw-cursor-pointer tw-text-gray-400 hover:tw-text-black"
                        >
                            ×
                        </button>

                        <h1 className="modal-title tw-mb-8">
                            LOGOUT
                        </h1>
                        
                        <p className="modal-text tw-text-2xl tw-mb-8">
                            Are you sure you want to logout?
                        </p>
                        
                        <button 
                            onClick={handleLogoutConfirm}
                            className="btn-primary tw-mb-4"
                        >
                            YES, LOGOUT
                        </button>
                        
                        <button 
                            onClick={handleLogoutCancel}
                            className="btn-secondary"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            )}
            </div>
        </>
    );
}

export default BadgesPage;
